import { CompanyResponse, FidelityCustomerDto } from "./dto/fidelityCustomer.dto";
import { UserDto } from "./dto/user.dto";
import { hideLoader } from "./uiFunction";
import { showLoader } from "./uiFunction";

interface PreSignedUrlResponse {
  url: string;
}

export type VerifyEmailPhoneRequest = {
  email?: string;
  phonePrefix?: string;
  phoneNumber?: string;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_PATH;

export enum RequestHeaders {
  CompanyId = "X-Company-Id",
  MenuId = "X-Menu-Id",
  SessionId = "X-Session-Id",
  RequestId = "X-Request-Id",
  ClientLanguage = "X-ClientLanguage-Id",
  Hostname = "X-Hostname",
}

export interface ApiError extends Error {
  statusCode?: number;
}

export async function fetchCompanyById(
  id: string,
  language: string
): Promise<CompanyResponse> {
  return withLoader(async () => {
  try {
    if (!id || !language) throw new Error("Id and language are required");
    const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [RequestHeaders.CompanyId]: id,
        [RequestHeaders.ClientLanguage]: language,
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data: CompanyResponse = await response.json();
    return data;
  } catch (error) {
      console.error("Error during API call:", error);
      throw error;
    }
  });
}

export async function createUser(
  user: UserDto,
  companyId: string,
  language: string
): Promise<FidelityCustomerDto> {
  return withLoader(async () => {
  try {
    if (!user || !companyId || !language) throw new Error("Invalid input");
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [RequestHeaders.CompanyId]: companyId,
        [RequestHeaders.ClientLanguage]: language,
      },
      body: JSON.stringify(user),
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `Error ${response.status}: ${response.statusText}`,
        statusCode: response.status,
        name: "ApiError",
      }
      throw error;
    }

    const data: FidelityCustomerDto = await response.json();
    return data;
  } catch (error) {
      console.error("Error during API call:", error);
      throw error;
    }
  });
}

export async function checkUser(
  request: VerifyEmailPhoneRequest,
  companyId: string,
  language: string
): Promise<boolean> {
  return withLoader(async () => {
    try {
      if (!request || !companyId || !language) throw new Error("Invalid input");
      const response = await fetch(`${API_BASE_URL}/auth/verify-email-phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          [RequestHeaders.CompanyId]: companyId,
          [RequestHeaders.ClientLanguage]: language,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data: { isAvailable: boolean } = await response.json();
      return data.isAvailable;
    } catch (error) {
      console.error("Error during API call:", error);
      throw error;
    }
  });
}

export async function fetchPreSignedUrl(
  fileName: string, 
  companyId: string
): Promise<string> {
  return withLoader(async () => {
    try {
      const preSignerUrl = `presigned-get-url`;
      const url = `${API_BASE_URL}/bucket/${preSignerUrl}?filename=${fileName}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          [RequestHeaders.CompanyId]: companyId || "",
        },
      });

      if (!response.ok) {
        throw new Error(`Errore nella richiesta: ${response.statusText}`);
      }

      const data: PreSignedUrlResponse = await response.json();
      return data.url;
    } catch (error) {
      console.error("Errore nel recupero del pre-signed URL:", error);
      throw error;
    }
  });
}

export function createBucketFilename(s3FeaturePath: string, fileName: string): string {
  return `${s3FeaturePath}/${fileName}`;
}

export async function withLoader<T>(fn: () => Promise<T>): Promise<T> {
  showLoader();
  try {
    return await fn();
  } finally {
    hideLoader();
  }
}